import type { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { currency } from "@/client/lib/format";
import { calcAdjustedDamage } from "@/lib/calc";
import { FieldPair, Section } from "../view/section";
import { CurrencyInput, FormField, FormInput } from "./form-field";
import type { InsuranceFormData } from "./types";

interface DamageCalcOutput {
  adjustedDamage: number;
  reinstatementMonths: number;
  inflationAmtYear1: number;
  inflationAmtYear2: number;
  inflationAmtYear3: number;
  inflationAmtYear4: number;
  reinstatementInflationTotal: number;
}

interface Props {
  register: UseFormRegister<InsuranceFormData>;
  watch: UseFormWatch<InsuranceFormData>;
  setValue: UseFormSetValue<InsuranceFormData>;
  damageCalc: DamageCalcOutput | null;
}

export function SectionDamageForm({ register, watch, setValue, damageCalc }: Props): React.ReactElement {
  const damageAmount = watch("damageAmount");
  const changePercent = watch("damageChangePercentage");
  const adjusted = damageAmount != null ? calcAdjustedDamage(damageAmount, changePercent ?? 0) : null;
  const hasValue = adjusted != null && adjusted !== 0;

  return (
    <Section id="damage" title="Damage Provision">
      <div className="grid gap-3 md:grid-cols-2 mb-3">
        <FormField label="Damage Amount" htmlFor="damageAmount">
          <CurrencyInput
            id="damageAmount"
            value={damageAmount}
            onChange={(v) => setValue("damageAmount", v, { shouldDirty: true })}
          />
        </FormField>
        <FormField label="Change %" htmlFor="damageChangePercentage" hint="blank defaults to 0">
          <FormInput
            id="damageChangePercentage"
            type="number"
            step="0.01"
            {...register("damageChangePercentage", { valueAsNumber: true })}
          />
        </FormField>
      </div>

      <h4 className="text-xs font-medium text-muted-foreground mb-2">
        Month Overrides (inherit from Financial Config)
      </h4>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <FormField label="First Year Mo" htmlFor="damageFirstYearMonths">
          <FormInput
            id="damageFirstYearMonths"
            type="number"
            placeholder="inherit"
            {...register("damageFirstYearMonths", { valueAsNumber: true })}
          />
        </FormField>
        <FormField label="Demo/Plan Mo" htmlFor="damageDemoPlanMonths">
          <FormInput
            id="damageDemoPlanMonths"
            type="number"
            placeholder="inherit"
            {...register("damageDemoPlanMonths", { valueAsNumber: true })}
          />
        </FormField>
        <FormField label="Construction Mo" htmlFor="damageConstructionMonths">
          <FormInput
            id="damageConstructionMonths"
            type="number"
            placeholder="inherit"
            {...register("damageConstructionMonths", { valueAsNumber: true })}
          />
        </FormField>
      </div>

      <h4 className="text-xs font-medium text-muted-foreground mb-2">Inflation % Overrides</h4>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <FormField label="Year 1 %" htmlFor="damageInflationYear1Pct">
          <FormInput
            id="damageInflationYear1Pct"
            type="number"
            step="0.01"
            placeholder="inherit"
            {...register("damageInflationYear1Pct", { valueAsNumber: true })}
          />
        </FormField>
        <FormField label="Year 2 %" htmlFor="damageInflationYear2Pct">
          <FormInput
            id="damageInflationYear2Pct"
            type="number"
            step="0.01"
            placeholder="inherit"
            {...register("damageInflationYear2Pct", { valueAsNumber: true })}
          />
        </FormField>
        <FormField label="Year 3 %" htmlFor="damageInflationYear3Pct">
          <FormInput
            id="damageInflationYear3Pct"
            type="number"
            step="0.01"
            placeholder="inherit"
            {...register("damageInflationYear3Pct", { valueAsNumber: true })}
          />
        </FormField>
        <FormField label="Year 4 %" htmlFor="damageInflationYear4Pct">
          <FormInput
            id="damageInflationYear4Pct"
            type="number"
            step="0.01"
            placeholder="inherit"
            {...register("damageInflationYear4Pct", { valueAsNumber: true })}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-md bg-muted/50">
        <FieldPair label="Adjusted Damage" value={hasValue ? currency(adjusted) : "N/A"} />
        <FieldPair label="Reinst Months" value={damageCalc ? String(damageCalc.reinstatementMonths) : "—"} />
        <FieldPair label="Inflation Y1" value={damageCalc ? currency(damageCalc.inflationAmtYear1) : "—"} />
        <FieldPair label="Inflation Y2" value={damageCalc ? currency(damageCalc.inflationAmtYear2) : "—"} />
        <FieldPair label="Inflation Y3" value={damageCalc ? currency(damageCalc.inflationAmtYear3) : "—"} />
        <FieldPair label="Inflation Y4" value={damageCalc ? currency(damageCalc.inflationAmtYear4) : "—"} />
        <FieldPair
          label="Inflation Total"
          value={damageCalc ? currency(damageCalc.reinstatementInflationTotal) : "—"}
        />
      </div>
    </Section>
  );
}
